import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ImagePlus, Loader2, Sparkles, Wand2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LibraryPickerModal } from "@/components/media/library-picker-modal";
import {
  estimateDraftUsd,
  formatUsd,
  useAiVideoOptions,
  useGenerateVideoDraft,
  useWriteVideoBrief,
  type AiVideoAspectRatio,
  type AiVideoResolution,
} from "@/hooks/use-ai-video";

export interface GenerateVideoInitial {
  prompt?: string | null;
  model?: string | null;
  aspectRatio?: AiVideoAspectRatio | null;
  durationSeconds?: number | null;
  resolution?: AiVideoResolution | null;
  referenceAssetIds?: string[];
}

interface GenerateVideoDialogProps {
  brandId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefill (e.g. "Try again" on an existing AI video). */
  initial?: GenerateVideoInitial | null;
}

interface Reference {
  id: string;
  url: string | null;
}

const SHAPES: { value: AiVideoAspectRatio; labelKey: string }[] = [
  { value: "9:16", labelKey: "vertical" },
  { value: "1:1", labelKey: "square" },
  { value: "16:9", labelKey: "landscape" },
];

export function GenerateVideoDialog({ brandId, open, onOpenChange, initial }: GenerateVideoDialogProps) {
  const { t } = useTranslation("pieces");
  const options = useAiVideoOptions(brandId).data;
  const writeBrief = useWriteVideoBrief(brandId);
  const generate = useGenerateVideoDraft(brandId);

  const [idea, setIdea] = useState("");
  const [prompt, setPrompt] = useState("");
  const [modelId, setModelId] = useState("");
  const [aspectRatio, setAspectRatio] = useState<AiVideoAspectRatio>("9:16");
  const [durationSeconds, setDurationSeconds] = useState(8);
  const [resolution, setResolution] = useState<AiVideoResolution>("480p");
  const [references, setReferences] = useState<Reference[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Reset from defaults (or the prefill) each time the dialog opens.
  useEffect(() => {
    if (!open || !options) return;
    const d = options.defaults;
    setIdea("");
    setPrompt(initial?.prompt ?? "");
    setModelId(initial?.model && options.models.some((m) => m.id === initial.model) ? initial.model : d.model);
    setAspectRatio(initial?.aspectRatio ?? d.aspectRatio);
    setDurationSeconds(initial?.durationSeconds ?? d.durationSeconds);
    setResolution(initial?.resolution ?? d.resolution);
    setReferences((initial?.referenceAssetIds ?? []).map((id) => ({ id, url: null })));
  }, [open, options, initial]);

  const model = options?.models.find((m) => m.id === modelId);

  // Keep length and quality valid for the chosen model.
  useEffect(() => {
    if (!model) return;
    setDurationSeconds((s) => Math.min(model.maxSeconds, Math.max(model.minSeconds, s)));
    setResolution((r) => (model.resolutions.includes(r) ? r : model.resolutions[0]!));
  }, [model]);

  const lengths = useMemo(() => {
    if (!model) return [];
    return Array.from({ length: model.maxSeconds - model.minSeconds + 1 }, (_, i) => model.minSeconds + i);
  }, [model]);

  const refsAllowed = (model?.maxReferences ?? 0) > 0;
  const draftUsd = estimateDraftUsd(model, resolution, durationSeconds);
  const finishUsd = (options?.upscale.usdPerSecond ?? 0) * durationSeconds;
  const configured = options?.configured.generate ?? true;
  const busy = generate.isPending;

  async function handleWriteBrief() {
    try {
      const r = await writeBrief.mutateAsync({ idea, aspectRatio, durationSeconds });
      setPrompt(r.prompt);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("library.aiVideo.toast.briefFailed"));
    }
  }

  async function handleGenerate() {
    if (!model) return;
    try {
      await generate.mutateAsync({
        prompt: prompt.trim(),
        model: model.id,
        aspectRatio,
        durationSeconds,
        resolution,
        referenceAssetIds: refsAllowed ? references.slice(0, model.maxReferences).map((r) => r.id) : [],
      });
      toast.success(t("library.aiVideo.toast.started"));
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("library.aiVideo.toast.failed"));
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("library.aiVideo.title")}</DialogTitle>
            <DialogDescription>{t("library.aiVideo.description")}</DialogDescription>
          </DialogHeader>

          {!configured && (
            <p role="alert" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              {t("library.aiVideo.notConfigured")}
            </p>
          )}

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="ai-video-idea" className="block text-sm font-medium">
                {t("library.aiVideo.idea")}
              </label>
              <textarea
                id="ai-video-idea"
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                rows={2}
                placeholder={t("library.aiVideo.ideaPlaceholder")}
                className="block w-full rounded border px-3 py-2 text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleWriteBrief}
                disabled={idea.trim().length < 3 || writeBrief.isPending}
              >
                {writeBrief.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {t("library.aiVideo.writeShot")}
              </Button>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="ai-video-prompt" className="block text-sm font-medium">
                {t("library.aiVideo.prompt")}
              </label>
              <textarea
                id="ai-video-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder={t("library.aiVideo.promptPlaceholder")}
                className="block w-full rounded border px-3 py-2 text-sm"
              />
              <p className="text-xs text-muted-foreground">{t("library.aiVideo.promptHint")}</p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="ai-video-model" className="block text-sm font-medium">
                  {t("library.aiVideo.model")}
                </label>
                <select
                  id="ai-video-model"
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                  className="block w-full rounded border px-3 py-2 text-sm"
                >
                  {options?.models.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <span className="block text-sm font-medium">{t("library.aiVideo.shape")}</span>
                <div role="group" aria-label={t("library.aiVideo.shape")} className="flex gap-1">
                  {SHAPES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      aria-pressed={aspectRatio === s.value}
                      onClick={() => setAspectRatio(s.value)}
                      className={`flex-1 rounded border px-2 py-2 text-xs ${
                        aspectRatio === s.value ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"
                      }`}
                    >
                      {t(`library.aiVideo.shapes.${s.labelKey}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <label htmlFor="ai-video-length" className="block text-sm font-medium">
                  {t("library.aiVideo.length")}
                </label>
                <select
                  id="ai-video-length"
                  value={durationSeconds}
                  onChange={(e) => setDurationSeconds(Number(e.target.value))}
                  className="block w-full rounded border px-3 py-2 text-sm"
                >
                  {lengths.map((s) => (
                    <option key={s} value={s}>{t("library.aiVideo.seconds", { count: s })}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label htmlFor="ai-video-quality" className="block text-sm font-medium">
                  {t("library.aiVideo.quality")}
                </label>
                <select
                  id="ai-video-quality"
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value as AiVideoResolution)}
                  className="block w-full rounded border px-3 py-2 text-sm"
                >
                  {model?.resolutions.map((r) => (
                    <option key={r} value={r}>
                      {r === "480p" ? t("library.aiVideo.draft480") : r}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {refsAllowed && (
              <div className="space-y-1.5">
                <span className="block text-sm font-medium">{t("library.aiVideo.references")}</span>
                <p className="text-xs text-muted-foreground">
                  {t("library.aiVideo.referencesHint", { max: model!.maxReferences })}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {references.map((r) => (
                    <div key={r.id} className="relative h-14 w-14 overflow-hidden rounded border bg-muted">
                      {r.url ? (
                        <img src={r.url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <ImagePlus className="m-auto mt-4 h-5 w-5 text-muted-foreground" />
                      )}
                      <button
                        type="button"
                        onClick={() => setReferences((prev) => prev.filter((x) => x.id !== r.id))}
                        aria-label={t("library.aiVideo.removeReference")}
                        className="absolute right-0.5 top-0.5 rounded-full bg-background/90 p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {references.length < model!.maxReferences && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                      <ImagePlus className="h-4 w-4" />
                      {t("library.aiVideo.addReferences")}
                    </Button>
                  )}
                </div>
              </div>
            )}

            <p className="rounded bg-muted px-3 py-2 text-sm" data-testid="ai-video-price">
              {t("library.aiVideo.price", { draft: formatUsd(draftUsd), finish: formatUsd(finishUsd) })}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              {t("library.aiVideo.cancel")}
            </Button>
            <Button onClick={handleGenerate} disabled={busy || !configured || !model || prompt.trim().length === 0}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {t("library.aiVideo.generate", { price: formatUsd(draftUsd) })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LibraryPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        brandId={brandId}
        type="IMAGE"
        multi
        max={model?.maxReferences}
        selectedUrls={references.map((r) => r.url).filter(Boolean) as string[]}
        onConfirm={(_urls, assets) => {
          setReferences(assets.map((a) => ({ id: a.id, url: a.url })));
          setPickerOpen(false);
        }}
      />
    </>
  );
}
